import type { TableColumnStaticProps } from '@/types/tableTypes';
import { buildEntityColumns } from '@/composables/entityColumns';
import {
  getSearchTableCustomColumns,
  TRACK_MATCHING_CUSTOM_COLUMNS,
} from '@/composables/searchTableCustomColumns';
import {
  AUDIO_FILE_ISSUE_COLUMNS,
  AUDIO_FILE_ISSUE_DEFAULT_WIDTHS,
  AUDIO_FILE_ISSUES_ENTITY_ID,
} from '@/composables/audioFileIssuesSchema';
import {
  PLAYLIST_SWITCHING_COLUMNS,
  PLAYLIST_SWITCHING_DEFAULT_WIDTHS,
  PLAYLIST_SWITCHING_ENTITY_ID,
} from '@/composables/playlistSwitchingSchema';

/**
 * Report pages whose filterable/sortable columns span multiple tables (or are
 * SQL aggregates) and therefore have no single Eloquent-model `models.json`
 * backing. They supply a hand-rolled column schema keyed by a synthetic entity
 * id, mirroring the matching backend FilterSchema class. Add a page here to get
 * the shared persistent-columns + filter-builder behaviour.
 *
 * Lives here rather than inside `usePersistentTable` because saved-view
 * expansion needs the same map without pulling in a composable that calls
 * `useRoute()` — a pure function must stay callable outside a component.
 */
export const PAGE_LEVEL_SCHEMAS: Record<string, TableColumnStaticProps[]> = {
  [AUDIO_FILE_ISSUES_ENTITY_ID]: AUDIO_FILE_ISSUE_COLUMNS,
  [PLAYLIST_SWITCHING_ENTITY_ID]: PLAYLIST_SWITCHING_COLUMNS,
};

/**
 * Per-page starting widths. Applying a saved view replaces `columnSize`
 * wholesale, so expansion has to write a width for EVERY column in the
 * universe — falling back to a bare 150 would silently flatten these two pages'
 * default layouts the first time anyone loaded a view.
 */
const PAGE_LEVEL_DEFAULT_WIDTHS: Record<string, Record<string, number>> = {
  [AUDIO_FILE_ISSUES_ENTITY_ID]: AUDIO_FILE_ISSUE_DEFAULT_WIDTHS,
  [PLAYLIST_SWITCHING_ENTITY_ID]: PLAYLIST_SWITCHING_DEFAULT_WIDTHS,
};

/** The width a column gets when neither the view nor the page names one. */
export const DEFAULT_COLUMN_WIDTH = 150;

/**
 * Store keys that are NOT the id their columns are built from.
 *
 * The persistent-table STATE for the track-matching page is scoped under
 * `TrackMatching` (persistentTableStore), while its COLUMNS come from
 * `LiveTrack` (usePersistentTable). Saved views take the store key, so without
 * this map `entityColumnUniverse('TrackMatching')` returned an EMPTY array —
 * every Track Matching view saved no columns, no pins and no widths, and loading
 * one wrote five empty maps over the live layout: everything visible, every pin
 * dropped, every width back to 150, the sort gone.
 */
const COLUMN_SOURCE_FOR_STORE_KEY: Record<string, string> = {
  TrackMatching: 'LiveTrack',
};

/**
 * Custom columns a PAGE injects, keyed by store scope.
 *
 * `getSearchTableCustomColumns` only knows the search page's own additions — one
 * Play column for LiveTrack. Track Matching prepends SIX (actions, play, history,
 * source, live_track_id, upc) through `usePersistentTable({ additionalColumns })`,
 * and five of them were invisible here: a user who hid Source, widened History or
 * pinned Live Track Id had all three thrown away on save, because a column outside
 * the universe never reaches the definition and `applyColumnConfigSnapshot`
 * replaces all five maps wholesale.
 */
const PAGE_CUSTOM_COLUMNS_FOR_STORE_KEY: Record<string, TableColumnStaticProps[]> = {
  TrackMatching: TRACK_MATCHING_CUSTOM_COLUMNS,
};

/**
 * Every column that exists for an entity, in the same order and with the same
 * membership `usePersistentTable` builds for the table itself. This is what makes
 * "drop unknown column ids" possible: a view saved six months ago may name
 * columns that have since been renamed away, and those have to be skipped rather
 * than thrown on.
 *
 * It has to agree with the table's own column list, or a saved view silently
 * loses whatever the two disagree about — which is what happened twice: once for
 * the whole Track Matching page, and once for the custom columns a page injects
 * (Play, Currently Playing), which live outside models.json.
 */
const universeByEntityId = new Map<string, TableColumnStaticProps[]>();

export function entityColumnUniverse(entityId: string): TableColumnStaticProps[] {
  // Computed once per entity. The answer is a pure function of `entityId` over
  // module constants and a static JSON import — nothing here changes at runtime —
  // and pruneToDefinition calls this on every evaluation of `liveDefinition`, which
  // with a saved view loaded is once per mousemove of a column-resize drag. That
  // rebuilt eighty column objects out of models.json each time.
  //
  // The array is SHARED, so callers must read it and not mutate it. All three do
  // today, and it was already true of the page-level schemas this returns directly.
  const cached = universeByEntityId.get(entityId);
  if (cached) {
    return cached;
  }

  const schemaColumns = schemaColumnsFor(entityId);
  if (schemaColumns.length === 0) {
    // Not cached, for a reason that is about the map rather than the answer: an
    // empty universe means models.json has never heard of this entity, and entity
    // strings arrive from saved-view rows and from the URL. Caching misses would let
    // any string anyone types grow this map for the life of the page. Recomputing a
    // miss costs one failed lookup.
    return [];
  }

  // Custom columns are prepended by usePersistentTable's baseColumns, so they are
  // prepended here too — their ordinals have to line up with the table's.
  //
  // Deliberately AFTER the page-level branch rather than inside it: an earlier
  // shape returned the page-level schema early and never reached this merge, so
  // the first custom column added to Audio File Issues or Playlist Switching
  // would have reproduced the exact bug this map exists to fix — and adding an
  // entry to the map, the obvious fix, would have had no effect.
  const universe = [...customColumnsFor(entityId), ...schemaColumns];
  universeByEntityId.set(entityId, universe);

  return universe;
}

/** The entity's own columns: a page's hand-rolled schema, or models.json. */
function schemaColumnsFor(entityId: string): TableColumnStaticProps[] {
  const pageLevelColumns = PAGE_LEVEL_SCHEMAS[entityId];
  if (pageLevelColumns) {
    return pageLevelColumns;
  }

  try {
    return buildEntityColumns(COLUMN_SOURCE_FOR_STORE_KEY[entityId] ?? entityId);
  } catch {
    // An entity string with no models.json entry — a stale link, or a model
    // removed since the view was saved. An empty universe makes expansion a
    // no-op rather than a crash.
    return [];
  }
}

/** Columns a PAGE injects on top of the schema, for any scope that has them. */
function customColumnsFor(entityId: string): TableColumnStaticProps[] {
  const pageColumns = PAGE_CUSTOM_COLUMNS_FOR_STORE_KEY[entityId];
  if (pageColumns) {
    return pageColumns;
  }

  return getSearchTableCustomColumns(COLUMN_SOURCE_FOR_STORE_KEY[entityId] ?? entityId);
}

/** The width this entity gives a column before any user or view touches it. */
export function defaultWidthFor(entityId: string, columnId: string): number {
  return PAGE_LEVEL_DEFAULT_WIDTHS[entityId]?.[columnId] ?? DEFAULT_COLUMN_WIDTH;
}
